import { SectionMessage } from "@certn/cascadia-react";
import { ActivityLogTab, type ActivityLogFilterState } from "@certn/monitoring";
import { useMemo, useState } from "react";
import type { SortDescriptor } from "react-aria-components";
import { useTranslation } from "react-i18next";

import {
  buildApplicantActivityLogQuery,
  useApplicantMonitoringActivityLog,
  useApplicantMonitoringClientDetail,
} from "./useMonitoringApi";

const PAGE_SIZE = 25;

const EMPTY_FILTERS: ActivityLogFilterState = {
  search: "",
  checkTypes: [],
  monitorStatuses: [],
  resultStatuses: [],
  dateRange: null,
};

export function ApplicantActivityLogTab({
  customerAccountId,
}: {
  customerAccountId: string | undefined;
}) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<ActivityLogFilterState>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "run_date",
    direction: "descending",
  });

  const { data: detail } =
    useApplicantMonitoringClientDetail(customerAccountId);
  const query = useMemo(
    () =>
      buildApplicantActivityLogQuery(page, PAGE_SIZE, filters, sortDescriptor),
    [filters, page, sortDescriptor]
  );
  const { data, isLoading, error } = useApplicantMonitoringActivityLog(
    customerAccountId,
    query
  );

  if (error) {
    return (
      <SectionMessage
        type="danger"
        testID="monitoring-activity-log-error"
        sectionTitle={t("Unable to load activity log")}
      >
        {t("Please try again later.")}
      </SectionMessage>
    );
  }

  return (
    <ActivityLogTab
      rows={data?.results ?? []}
      count={data?.count ?? 0}
      isLoading={isLoading}
      page={page}
      pageSize={PAGE_SIZE}
      onPageChange={setPage}
      filters={filters}
      onFiltersChange={setFilters}
      onResetPage={() => setPage(1)}
      checkOptions={detail?.enrollments ?? []}
      sortDescriptor={sortDescriptor}
      onSortChange={setSortDescriptor}
      description={t(
        "Below are historical results for checks monitored by your employer."
      )}
      renderCaseLink={(shortId) => shortId}
    />
  );
}
